import { buildTerrain, type BoatDef, type LevelDef, type Shape } from './levels';
import { UNIT_H, UNIT_HALF_W } from './physics';
import { buildWalkGraph, deathLine, isConnected, levelSolid, nodeNear, type SolidFn, type WalkGraph } from './pathing';

/**
 * Every map must have a way across: a mole at one team's first spawn can walk (jump, step, drop)
 * to the other team's. Where the ground is split, a plank bridge goes in between the closest two
 * places the two halves come to, with posts under it; where the ground is in the way (a mountain,
 * a wall) a tunnel is dug through it instead, and across open water a chain of small rafts is moored
 * between the two decks. Run by the war-map builder and by the generator; the
 * authored maps get their crossings written out by hand and are checked by test.
 */

export const BRIDGE_THICKNESS = 14;
/** Steepest plank worth laying, rise over run. */
const MAX_SLOPE = 0.75;
/** Longest plank worth laying. */
const MAX_SPAN = 900;
/** Longest tunnel worth digging, and its steepest climb. */
const MAX_TUNNEL = 700;
const TUNNEL_SLOPE = 0.45;
/** A tunnel is a chain of round holes this big, this far apart. */
const TUNNEL_R = 34;
const TUNNEL_STEP = 18;

export interface Crossing {
  from: number;
  to: number;
  kind: 'plank' | 'tunnel' | 'pontoons';
}

/** A chain of rafts this long a panel and this far apart carries a mole over open water. */
const PONTOON_PANEL = 90;
const PONTOON_GAP = 56;

/**
 * The cheapest way from one standing place to another: walking edges are nearly free, a plank (a
 * made-up edge between two standing places not too far or steep, with nothing in the way above it)
 * costs by its length, and a tunnel (the same where something is in the way) costs a good deal
 * more. Returns the crossings the route needs; empty when it needs none; null when no route exists.
 */
export function cheapestRoute(g: WalkGraph, from: number, to: number, solid: SolidFn, waterY: number | null = null): Crossing[] | null {
  const n = g.nodes.length;
  /** A node standing on a raft deck at the usual height. */
  const onDeck = (i: number): boolean => waterY !== null && Math.abs(g.nodes[i]!.y - (waterY - 40)) <= 50;
  const clear = new Map<number, boolean>();
  const corridorClear = (u: number, v: number): boolean => {
    const key = u * n + v;
    const hit = clear.get(key);
    if (hit !== undefined) return hit;
    const a = g.nodes[u]!;
    const b = g.nodes[v]!;
    const steps = Math.max(2, Math.ceil(Math.abs(b.x - a.x) / 8));
    let ok = true;
    for (let i = 1; i < steps && ok; i++) {
      const t = i / steps;
      const x = a.x + (b.x - a.x) * t;
      const y = a.y + (b.y - a.y) * t - 4;
      for (let dy = 6; dy < UNIT_H; dy += 8) {
        if (solid(x - UNIT_HALF_W * 0.6, y - dy) || solid(x + UNIT_HALF_W * 0.6, y - dy)) {
          ok = false;
          break;
        }
      }
    }
    clear.set(key, ok);
    return ok;
  };
  const dist = new Float64Array(n).fill(Infinity);
  const prev = new Int32Array(n).fill(-1);
  /** 0 walked, 1 plank, 2 tunnel, 3 pontoons. */
  const via = new Uint8Array(n);
  const done = new Uint8Array(n);
  dist[from] = 0;
  for (;;) {
    let u = -1;
    let best = Infinity;
    for (let i = 0; i < n; i++) {
      if (!done[i] && dist[i]! < best) {
        best = dist[i]!;
        u = i;
      }
    }
    if (u < 0 || u === to) break;
    done[u] = 1;
    const nu = g.nodes[u]!;
    for (const v of g.edges[u]!) {
      const nv = g.nodes[v]!;
      const c = best + 1 + Math.hypot(nv.x - nu.x, nv.y - nu.y) * 0.05;
      if (c < dist[v]!) {
        dist[v] = c;
        prev[v] = u;
        via[v] = 0;
      }
    }
    for (let v = 0; v < n; v++) {
      if (done[v]) continue;
      const nv = g.nodes[v]!;
      const dx = Math.abs(nv.x - nu.x);
      if (dx < 24 || dx > MAX_SPAN) continue;
      const dy = Math.abs(nv.y - nu.y);
      const slope = dy / dx;
      if (waterY !== null && dy <= 40 && onDeck(u) && onDeck(v) && dx > 140) {
        const c = best + 90 + dx * 1.2 + dy * 4;
        if (c < dist[v]! && corridorClear(u, v)) {
          dist[v] = c;
          prev[v] = u;
          via[v] = 3;
          continue;
        }
      }
      if (slope <= MAX_SLOPE) {
        const c = best + 150 + dx * 2 + dy * 4;
        if (c < dist[v]! && corridorClear(u, v)) {
          dist[v] = c;
          prev[v] = u;
          via[v] = 1;
          continue;
        }
      }
      if (dx <= MAX_TUNNEL && slope <= TUNNEL_SLOPE) {
        const c = best + 500 + dx * 3 + dy * 6;
        if (c < dist[v]!) {
          dist[v] = c;
          prev[v] = u;
          via[v] = 2;
        }
      }
    }
  }
  if (!isFinite(dist[to]!)) return null;
  const out: Crossing[] = [];
  for (let v = to; v !== from && v >= 0; v = prev[v]!) if (via[v]) out.push({ from: prev[v]!, to: v, kind: via[v] === 2 ? 'tunnel' : via[v] === 3 ? 'pontoons' : 'plank' });
  return out.reverse();
}

/** A plank from (x0, y0) to (x1, y1) (the walking surface) plus posts under it every so often. */
export function plankShapes(level: LevelDef, x0: number, y0: number, x1: number, y1: number): Shape[] {
  const lo = x0 <= x1 ? { x: x0, y: y0 } : { x: x1, y: y1 };
  const hi = x0 <= x1 ? { x: x1, y: y1 } : { x: x0, y: y0 };
  const slope = (hi.y - lo.y) / Math.max(1, hi.x - lo.x);
  // Run the ends a little into whatever they meet, so there is no step.
  const ax = lo.x - 10;
  const bx = hi.x + 10;
  const ay = lo.y - slope * 10;
  const by = hi.y + slope * 10;
  const shapes: Shape[] = [
    {
      kind: 'poly',
      pts: [[ax, ay], [bx, by], [bx, by + BRIDGE_THICKNESS], [ax, ay + BRIDGE_THICKNESS]].map(([x, y]) => [Math.round(x!), Math.round(y!)] as [number, number]),
      m: 'wood',
    },
  ];
  const floor = level.waterY ?? level.height;
  const span = hi.x - lo.x;
  const posts = Math.floor(span / 220);
  for (let i = 1; i <= posts; i++) {
    const x = lo.x + (span * i) / (posts + 1);
    const top = lo.y + slope * (x - lo.x) + BRIDGE_THICKNESS;
    const bottom = Math.min(floor + 40, top + 420);
    if (bottom - top > 24) shapes.push({ kind: 'rect', x: Math.round(x - 6), y: Math.round(top), w: 12, h: Math.round(bottom - top), m: 'wood' });
  }
  return shapes;
}

/** A tunnel along the line from (x0, y0) to (x1, y1), the floor on the line: round holes with room for a mole to stand in each. */
export function tunnelShapes(x0: number, y0: number, x1: number, y1: number): Shape[] {
  const len = Math.hypot(x1 - x0, y1 - y0);
  const n = Math.max(1, Math.ceil(len / TUNNEL_STEP));
  const holes: Shape[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    holes.push({ kind: 'hole', x: Math.round(x0 + (x1 - x0) * t), y: Math.round(y0 + (y1 - y0) * t - 30), r: TUNNEL_R });
  }
  return holes;
}

/** Small rafts moored in a row from one deck to the next, with a short gap between each. */
export function pontoonBoats(level: LevelDef, x0: number, x1: number, firstId: number): BoatDef[] {
  const lo = Math.min(x0, x1);
  const span = Math.abs(x1 - x0);
  const n = Math.max(1, Math.round((span - PONTOON_GAP) / (2 * PONTOON_PANEL + PONTOON_GAP)));
  const length = (span - PONTOON_GAP * (n + 1)) / n;
  const panelW = Math.max(50, Math.round(length / 2));
  const y = (level.waterY ?? 940) - 40;
  return Array.from({ length: n }, (_, i): BoatDef => ({
    id: `pontoon-${firstId + i}`,
    x: Math.round(lo + PONTOON_GAP + length / 2 + i * (length + PONTOON_GAP)),
    y,
    panelW,
    panels: 2,
    stops: 'none',
  }));
}

export interface Crossings {
  shapes: Shape[];
  boats: BoatDef[];
}

/**
 * Bridges, tunnels and pontoons that make both teams' first spawns reachable from each other
 * (nothing when they already are, or when no sensible crossing exists). Deterministic: the same
 * level always gets the same ones.
 */
export function bridgeShapes(level: LevelDef): Crossings {
  const added: Crossings = { shapes: [], boats: [] };
  const left = level.spawns[0]?.[0];
  const right = level.spawns[1]?.[0];
  if (!left || !right) return added;
  let current = level;
  for (let round = 0; round < 4; round++) {
    const terrain = buildTerrain(current);
    const solid = levelSolid(current, terrain);
    // Most levels are already connected: say so with the cheapest graph that can.
    if (isConnected(current, solid)) return added;
    const g = buildWalkGraph(current, solid);
    const ia = nodeNear(g, left[0], left[1]);
    const ib = nodeNear(g, right[0], right[1]);
    if (ia < 0 || ib < 0) return added;
    // Left to right first, then the way home.
    const out = cheapestRoute(g, ia, ib, solid, current.waterY) ?? [];
    const todo = out.length ? out : cheapestRoute(g, ib, ia, solid, current.waterY) ?? [];
    if (todo.length === 0) return added;
    const made: Crossings = { shapes: [], boats: [] };
    for (const c of todo) {
      const a = g.nodes[c.from]!;
      const b = g.nodes[c.to]!;
      if (Math.max(a.y, b.y) > deathLine(current) - 20) continue;
      if (c.kind === 'pontoons') made.boats.push(...pontoonBoats(current, a.x, b.x, level.boats.length + added.boats.length + made.boats.length + 1));
      else made.shapes.push(...(c.kind === 'tunnel' ? tunnelShapes(a.x, a.y, b.x, b.y) : plankShapes(current, a.x, a.y, b.x, b.y)));
    }
    if (made.shapes.length === 0 && made.boats.length === 0) return added;
    added.shapes.push(...made.shapes);
    added.boats.push(...made.boats);
    current = { ...current, shapes: [...current.shapes, ...made.shapes], boats: [...current.boats, ...made.boats] };
  }
  return added;
}
